import { Suspense, lazy } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
// layouts
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import DashboardLayout from '../layouts/dashboard';
// components
import AuthGuard from 'src/guards/AuthGuard';
import LoadingScreen from '../components/LoadingScreen';
import GuestGuard from '../guards/GuestGuard';
import MainLayout from '../layouts/main';
import LoginPage from '../pages/auth/LoginPage';
import RegisterPage from '../pages/auth/RegisterPage';
import TodosApp from 'src/pages/dashboard/todos/todos-app';
import AdminSettingsApp from 'src/pages/dashboard/admin-settings/admin-setting-app';
import { ManageUserBankAccountsApp } from 'src/pages/dashboard/users/manage-user-bank-accounts';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: '/',
      element: <Navigate to="/dashboard/home" replace />,
    },
    { path: '/dashboard', element: <Navigate to="/dashboard/home" replace /> },
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: 'home', element: <HomePage /> },
        { path: 'todos', element: <TodosApp /> },
        { path: 'admin-settings', element: <AdminSettingsApp /> },
        {
          path: 'funds',
          children: [
            { element: <Navigate to="/dashboard/funds/manage" replace />, index: true },
            { path: 'manage', element: <FundsApp /> },
            { path: 'mine', element: <MineFundsApp /> },
            { path: 'mine/:userFundId', element: <MineFundApp /> },
          ],
        },
        {
          path: 'user',
          children: [
            { element: <Navigate to="/dashboard/user/account" replace />, index: true },
            { path: 'account', element: <UserAccount /> },
            { path: 'validationReport', element: <MyValidationReportApp /> },
          ],
        },
        {
          path: 'users',
          children: [
            { element: <Navigate to="/dashboard/users/manage" replace />, index: true },
            { path: 'manage', element: <ManageUsers /> },
            { path: 'funds/:userId', element: <ManageUsersFunds /> },
            { path: 'payment-history/:userId', element: <PaymentHistoriesApp /> },
            { path: 'memberships/:userId', element: <MembershipsApp /> },
            { path: 'user-bank-accounts/:userId', element: <ManageUserBankAccountsApp /> },
            { path: 'user-warranties/:userId', element: <ManageUserWarrantiesApp /> },
          ],
        },
        {
          path: 'tiles',
          children: [
            { element: <Navigate to="/dashboard/tiles/manage" replace />, index: true },
            { path: 'manage', element: <ManageTiles /> },
          ],
        },
        {
          path: 'drive',
          children: [
            { element: <Navigate to="/dashboard/drive/manage" replace />, index: true },
            { path: 'manage', element: <ManageDriveApp /> },
          ],
        },
        {
          path: 'bank-accounts',
          children: [
            { element: <Navigate to="/dashboard/bank-accounts/manage" replace />, index: true },
            { path: 'manage', element: <BankAccountsApp /> },
          ],
        },
        {
          path: 'notifications',
          children: [
            { element: <Navigate to="/dashboard/notifications/list" replace />, index: true },
            { path: 'list', element: <NotificationsApp /> },
            { path: 'manage', element: <ManageNotifications /> },
            { path: ':id', element: <Notification /> },
          ],
        },
        {
          path: 'payment-documents',
          children: [
            { element: <Navigate to="/dashboard/payment-documents/list" replace />, index: true },
            { path: 'list', element: <ListMyPaymentDocumentsApp />, index: true },
            { path: 'list/:paymentDocumentsDepositorsId', element: <MyPaymentDocumentApp />, index: true },
          ],
        },
        {
          path: 'admin-payment-documents',
          children: [
            { element: <Navigate to="/dashboard/admin-payment-documents/manage" replace />, index: true },
            { path: 'manage', element: <ManagePaymentDocumentApp />, index: true },
            { path: 'manage/:userFundId', element: <ManagePaymentDocument /> },
            { path: 'new', element: <NewPaymentDocumentApp /> },
          ],
        },
        { path: 'admin-monthly-report', element: <AdminMonthlyReportApp /> },
        { path: 'admin-debtors', element: <AdminDebtorsApp /> },
      ],
    },

    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <RegisterPage />
            </GuestGuard>
          ),
        },
      ],
    },
    {
      path: '/',
      element: <MainLayout />,
      children: [{ path: '/', element: <HomePage /> }],
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// Dashboard
const MyValidationReportApp = Loadable(
  lazy(() => import('../pages/dashboard/my-validation-report/my-validation-report-app'))
);

const HomePage = Loadable(lazy(() => import('../pages/dashboard/Home/Admin')));
const NotificationsApp = Loadable(lazy(() => import('../pages/dashboard/notifications/notifications-app')));
const ManageNotifications = Loadable(lazy(() => import('../pages/dashboard/notifications/admin-notifications-app')));
const ManageDriveApp = Loadable(lazy(() => import('../pages/dashboard/drive/manage-drive-app')));
const Notification = Loadable(lazy(() => import('../pages/dashboard/notifications/notification')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/user/user-account')));
const ManageUsers = Loadable(lazy(() => import('../pages/dashboard/users/users-app')));
const ManageUserWarrantiesApp = Loadable(lazy(() => import('../pages/dashboard/users/manage-user-warranties-app')));
const ManageUsersFunds = Loadable(lazy(() => import('../pages/dashboard/users/user-funds-app')));
const PaymentHistoriesApp = Loadable(lazy(() => import('../pages/dashboard/payment-documents/payment-histories-app')));
const MembershipsApp = Loadable(lazy(() => import('../pages/dashboard/users-memberships/user-memberships-app')));
const BankAccountsApp = Loadable(lazy(() => import('../pages/dashboard/banks/bank-accounts-app')));
const FundsApp = Loadable(lazy(() => import('../pages/dashboard/funds/funds-app')));
const MineFundsApp = Loadable(lazy(() => import('../pages/dashboard/funds/mine-funds-app')));
const MineFundApp = Loadable(lazy(() => import('../pages/dashboard/funds/mine-fund-app')));
const ManageTiles = Loadable(lazy(() => import('../pages/dashboard/tiles/tiles-app')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));

const ManagePaymentDocumentApp = Loadable(
  lazy(() => import('../pages/dashboard/payment-documents/manage-payment-documents-app'))
);

const ListMyPaymentDocumentsApp = Loadable(
  lazy(() => import('../pages/dashboard/payment-documents/list-my-payment-documents-app'))
);

const MyPaymentDocumentApp = Loadable(
  lazy(() => import('../pages/dashboard/payment-documents/my-payment-document-app.jsx'))
);

const NewPaymentDocumentApp = Loadable(
  lazy(() => import('../pages/dashboard/payment-documents/manage-payment-documents-new'))
);

const ManagePaymentDocument = Loadable(
  lazy(() => import('../pages/dashboard/payment-documents/manage-payment-document'))
);

const AdminMonthlyReportApp = Loadable(lazy(() => import('../pages/dashboard/report/admin-monthly-report')));
const AdminDebtorsApp = Loadable(lazy(() => import('../pages/dashboard/deptors/admin-debtors')));
